import type {InvitedCreatorRow} from '../types/creatorTabTypes';

export interface InvitedMatchApiResponse {
  id: string;
  invitedAt?: string | null;
  invitedStatus?: string | null;
  origination?: string | null;
  profile?: InvitedMatchProfile | string | null;
  activeContract?: InvitedMatchContract | string | null;
}

interface InvitedMatchIdentity {
  cachedHandle?: string | null;
}

interface InvitedMatchProfile {
  id?: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  imageUrl?: string | null;
  email?: string | null;
  identities?: Array<InvitedMatchIdentity | string> | null;
}

interface InvitedMatchContract {
  id?: string;
  createdAt?: string | null;
}

function asProfile(
  value: InvitedMatchApiResponse['profile']
): InvitedMatchProfile | null {
  if (!value || typeof value === 'string') {
    return null;
  }
  return value;
}

function asContract(
  value: InvitedMatchApiResponse['activeContract']
): InvitedMatchContract | null {
  if (!value || typeof value === 'string') {
    return null;
  }
  return value;
}

function resolvedIdentities(
  identities: InvitedMatchProfile['identities']
): InvitedMatchIdentity[] {
  if (!identities?.length) {
    return [];
  }
  return identities.filter(
    (identity): identity is InvitedMatchIdentity =>
      Boolean(identity) && typeof identity === 'object'
  );
}

function creatorNameFromProfile(profile: InvitedMatchProfile | null): string {
  const displayName = profile?.displayName?.trim();
  if (displayName) {
    return displayName;
  }

  const fromParts = [profile?.firstName, profile?.lastName]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' ')
    .trim();
  if (fromParts) {
    return fromParts;
  }

  const handle = resolvedIdentities(
    profile?.identities
  )[0]?.cachedHandle?.trim();
  return handle || '';
}

export default function mapMatchToCreatorRow(
  match: InvitedMatchApiResponse
): InvitedCreatorRow {
  const profile = asProfile(match.profile);
  const contract = asContract(match.activeContract);
  const identities = resolvedIdentities(profile?.identities);

  return {
    id: match.id,
    profileId: profile?.id ?? '',
    creatorName: creatorNameFromProfile(profile),
    handle: identities[0]?.cachedHandle ?? null,
    avatarUrl: profile?.imageUrl ?? null,
    email: profile?.email ?? null,
    invitedAt: match.invitedAt ?? null,
    invitedStatus: match.invitedStatus ?? null,
    origination: match.origination ?? null,
    invitedAppliedAt: contract?.createdAt ?? null,
    paidGigId: contract?.id ?? null,
  };
}
